<script setup>
import { ref, watch, computed } from "vue";

/* ============================================================
   Props / Emits
============================================================ */
const props = defineProps({
  record: { type: Object, required: true }
});

const emit = defineEmits(["close"]);

/* ============================================================
   State
============================================================ */
const saving = ref(false);
const error = ref("");

// keep original identifier safe
const originalShopDomain = ref("");

// editable form model
const form = ref({
  shop_domain: "",
  cpapikey: "",
  active: false,

  sync_identities: false,
  sync_content: false,
  sync_price: false,
  sync_stock: false,
  sync_groups: false,
  sync_media: false,
  sync_types: false,
  sync_variants: false,
  sync_bricks: false,
});

const syncKeys = computed(() =>
  Object.keys(form.value).filter(k => k.startsWith("sync_"))
);
/* ============================================================
   Hydrate from record
============================================================ */
watch(
  () => props.record,
  (shop) => {
    if (!shop) return;

    originalShopDomain.value = shop.shop_domain;

    form.value.shop_domain = shop.shop_domain;
    form.value.cpapikey = shop.cpapikey;
    form.value.active = !!shop.active;

    Object.keys(form.value).forEach((key) => {
      if (key.startsWith("sync_")) {
        form.value[key] = !!shop[key];
      }
    });
  },
  { immediate: true }
);

/* ============================================================
   Helpers
============================================================ */
function apiHeaders() {
  return {
    "Content-Type": "application/json",
    "x-api-key": localStorage.getItem("api_key"),
  };
}

async function save() {
  saving.value = true;
  error.value = "";

  try {
    const res = await fetch(
      `/api/shops/nz/${encodeURIComponent(originalShopDomain.value)}`,
      {
        method: "PATCH",
        headers: apiHeaders(),
        body: JSON.stringify(form.value),
      }
    );

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || "Failed to save sync contract");
    }

    // return full updated record to caller
    emit("close", {
      ok: true,
      shop: json.shop
    });

  } catch (err) {
    error.value = err.message;
  } finally {
    saving.value = false;
  }
}

function cancel() {
  emit("close", false);
}
</script>

<template>
  <div class="sync-contract-editor">

    <!-- SHOP DOMAIN -->
    <div class="editor-field">
      <label>Domain</label>
      <input
        type="text"
        v-model="form.shop_domain"
        placeholder="shop.example.com"
      />
    </div>
    <!-- API KEY -->
    <div class="editor-field">
      <label>API Key</label>
      <input
        type="text"
        v-model="form.cpapikey"
        placeholder="someverylongsecretkey"
      />
    </div>
    <!-- ACTIVE -->
    <div class="editor-field">
      <label>
        <input type="checkbox" v-model="form.active" />
        Active
      </label>
    </div>
    <div><h3 class="sync-toggle-head">Sync Flags</h3></div>
    <!-- SYNC FLAGS -->
    <div class="sync-toggle-wrap">
      <div
        v-for="key in syncKeys"
        :key="key"
        class="sync-toggle"
      >
        <label>
          <input
            type="checkbox"
            v-model="form[key]"
            :disabled="key === 'sync_media' || key === 'sync_identities'"
          />
          {{ key.replace("sync_", "").replace("_", " ") }}
          <span v-if="key === 'sync_identities'" class="hint">(required)</span>
        </label>
      </div>
    </div>
    <!-- ACTIONS -->
    <div class="editor-actions">
      <button class="btn btn-warning" @click="cancel">
        Cancel
      </button>
      <button class="btn btn-primary" @click="save" :disabled="saving">
        {{ saving ? "Saving…" : "Save" }}
      </button>
    </div>

    <p v-if="error" class="error">{{ error }}</p>
  </div>
</template>

<style scoped lang="less">

h2{
  margin: 0px;
  
}

.editor-field {
  margin-bottom: 16px;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 4px;
  }

  input[type="text"] {
    width: 100%;
    padding: 6px 8px;
  }
}

.sync-toggle {
  margin-bottom: 12px;

  label {
    display: flex;
    gap: 10px;
    cursor: pointer;
  }

  input[type="checkbox"] {
    transform: scale(1.2);
  }
}

.editor-actions {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-top: 20px;
}
</style>
